import { useState, useEffect, useMemo, useRef } from 'react';
import {
  getCustomers,
  createCustomerAtomic,
  generateNoteNo,
  createPromissoryNote,
  createLegacyCustomerWithNote,
  createLegacyCustomerWithPlan,
  restoreDeletedCustomer,
} from '@/lib/supabaseDb';
import { capitalizeWords, digitsOnly } from '@/lib/inputHelpers';
import { useToast } from './Toast';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import { useAuth } from '@/contexts/AuthContext';
import ResponsiveFormModal from '@/components/base/ResponsiveFormModal';
import CurrencyInput from '@/components/base/CurrencyInput';
import CityDistrictSelect from '@/components/base/CityDistrictSelect';
import { formatTRY } from '@/lib/currencyFormat';
import { normalizeBookNo } from '@/lib/bookNo';
import LegacyInstallmentPlan, {
  type PaymentPlanType,
  type EqualPlanData,
  type CustomInstallmentRow,
  INITIAL_EQUAL_PLAN,
  generateEqualPlanInstallments,
  generateCustomPlanInstallments,
} from './LegacyInstallmentPlan';

type CustomerStatus = 'Aktif' | 'Borçlu' | 'Borcu Yok' | 'Kara Liste';

interface CustomerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (customerId: string) => void;
  zIndex?: number;
}

const DRAFT_KEY = 'polatlar_customer_form_draft';

export default function CustomerFormModal({ isOpen, onClose, onCreated, zIndex = 60 }: CustomerFormModalProps) {
  const { showToast } = useToast();
  const { triggerRefresh } = useDataRefresh();
  const { businessCity, businessDistrict } = useAuth();

  // Form state
  const [custFullName, setCustFullName] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [custSecondPhone, setCustSecondPhone] = useState('');
  const [custCustomerNo, setCustCustomerNo] = useState('');
  const [custCity, setCustCity] = useState('');
  const [custDistrict, setCustDistrict] = useState('');
  const [custNeighborhood, setCustNeighborhood] = useState('');
  const [custAddress, setCustAddress] = useState('');
  const [custIdentityNo, setCustIdentityNo] = useState('');
  const [custNotes, setCustNotes] = useState('');
  const [custStatus, setCustStatus] = useState<CustomerStatus>('Aktif');

  const [createNote, setCreateNote] = useState(false);
  const [noteAmount, setNoteAmount] = useState('');
  const [noteIssueDate, setNoteIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [noteDueDate, setNoteDueDate] = useState('');
  const [noteBookNo, setNoteBookNo] = useState('');
  const [notePageNo, setNotePageNo] = useState('');
  const [noteSequenceNo, setNoteSequenceNo] = useState('');
  const [noteGuarantorName, setNoteGuarantorName] = useState('');
  const [noteGuarantorPhone, setNoteGuarantorPhone] = useState('');
  const [noteDescription, setNoteDescription] = useState('');
  const [noteStatus, setNoteStatus] = useState('Bekliyor');

  const [isLegacyTransfer, setIsLegacyTransfer] = useState(false);
  const [legacyCustomerNo, setLegacyCustomerNo] = useState('');
  const [legacyPurchaseDate, setLegacyPurchaseDate] = useState('');
  const [legacyNoteNo, setLegacyNoteNo] = useState('');
  const [legacyBookNo, setLegacyBookNo] = useState('');
  const [legacyPageNo, setLegacyPageNo] = useState('');
  const [legacySequenceNo, setLegacySequenceNo] = useState('');
  const [legacyPrincipalAmount, setLegacyPrincipalAmount] = useState('');
  const [legacyPaidAmount, setLegacyPaidAmount] = useState('0');
  const [legacyIssueDate, setLegacyIssueDate] = useState('');
  const [legacyDueDate, setLegacyDueDate] = useState('');
  const [legacyUsePurchaseAsDue, setLegacyUsePurchaseAsDue] = useState(false);
  const [legacyGuarantorName, setLegacyGuarantorName] = useState('');
  const [legacyGuarantorPhone, setLegacyGuarantorPhone] = useState('');
  const [legacyDescription, setLegacyDescription] = useState('');
  const [legacyLastPaymentDate, setLegacyLastPaymentDate] = useState('');

  // Payment plan type states
  const [paymentPlanType, setPaymentPlanType] = useState<PaymentPlanType>('single_note');
  const [equalPlanData, setEqualPlanData] = useState<EqualPlanData>({ ...INITIAL_EQUAL_PLAN });
  const [customInstallmentData, setCustomInstallmentData] = useState<CustomInstallmentRow[]>([]);

  const [custErrors, setCustErrors] = useState<Record<string, string>>({});
  const [custTouched, setCustTouched] = useState(false);

  const [isOverdueNote, setIsOverdueNote] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const draftChecked = useRef(false);

  // Silinmiş müşteri dialog state
  const [deletedCustomerInfo, setDeletedCustomerInfo] = useState<any>(null);
  const [showDeletedCustomerDialog, setShowDeletedCustomerDialog] = useState(false);

  // Ref tabanlı anlık submit engelleme (state'ten daha hızlı)
  const submittingRef = useRef(false);

  const defaultNoteNo = useMemo(() => '', []);

  const isDirty = useMemo(() => {
    // custCity/custDistrict işletmenin varsayılan il/ilçesiyle otomatik
    // dolduruluyor (bkz. aşağıdaki useEffect) — bu, tek başına "kirli" form
    // sayılmamalı, aksi halde her açılışta gereksiz çıkış uyarısı çıkar.
    return custFullName !== '' || custPhone !== '' || custSecondPhone !== '' || custCustomerNo !== '' || custCity !== (businessCity || '') || custDistrict !== (businessDistrict || '') || custNeighborhood !== '' || custAddress !== '' || custIdentityNo !== '' || custNotes !== '' || custStatus !== 'Aktif' || isLegacyTransfer || createNote || paymentPlanType !== 'single_note' || equalPlanData.totalSaleAmount !== '' || equalPlanData.totalInstallmentCount !== '' || customInstallmentData.length > 0;
  }, [custFullName, custPhone, custSecondPhone, custCustomerNo, custCity, custDistrict, custNeighborhood, custAddress, custIdentityNo, custNotes, custStatus, isLegacyTransfer, createNote, paymentPlanType, equalPlanData, customInstallmentData, businessCity, businessDistrict]);

  const inputClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-300';
  const inputErrorClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-red-50 border border-red-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 text-foreground-800 placeholder:text-foreground-300';
  const selectClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 cursor-pointer';
  const labelClass = 'text-xs font-medium text-foreground-500 mb-1.5 block';
  const errorTextClass = 'text-xs text-red-500 mt-1';
  const readonlyClass = 'w-full px-3 py-2.5 text-sm sm:text-base bg-background-100 border border-background-200 rounded-lg text-foreground-600 cursor-not-allowed';
  const helperTextClass = 'text-[10px] text-foreground-300 mt-0.5';

  function resetForm() {
    setCustFullName(''); setCustPhone(''); setCustSecondPhone(''); setCustCustomerNo('');
    setCustCity(''); setCustDistrict(''); setCustNeighborhood(''); setCustAddress(''); setCustIdentityNo('');
    setCustNotes(''); setCustStatus('Aktif'); setCreateNote(false);
    setNoteAmount(''); setNoteIssueDate(new Date().toISOString().split('T')[0]);
    setNoteDueDate(''); setNoteBookNo(''); setNotePageNo(''); setNoteSequenceNo('');
    setNoteGuarantorName(''); setNoteGuarantorPhone(''); setNoteDescription(''); setNoteStatus('Bekliyor');
    setIsLegacyTransfer(false); setLegacyCustomerNo(''); setLegacyPurchaseDate(''); setLegacyNoteNo('');
    setLegacyBookNo(''); setLegacyPageNo(''); setLegacySequenceNo('');
    setLegacyPrincipalAmount(''); setLegacyPaidAmount('0');
    setLegacyIssueDate(''); setLegacyDueDate('');
    setLegacyUsePurchaseAsDue(false); setLegacyGuarantorName(''); setLegacyGuarantorPhone('');
    setLegacyDescription(''); setLegacyLastPaymentDate('');
    setPaymentPlanType('single_note'); setEqualPlanData({ ...INITIAL_EQUAL_PLAN }); setCustomInstallmentData([]);
    setCustErrors({}); setCustTouched(false); setIsSaving(false);
    setIsOverdueNote(false);
    submittingRef.current = false;
    setDeletedCustomerInfo(null);
    setShowDeletedCustomerDialog(false);
  }

  const saveTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (!isDirty) { sessionStorage.removeItem(DRAFT_KEY); return; }
      const draft = { custFullName, custPhone, custSecondPhone, custCustomerNo, custCity, custDistrict, custNeighborhood, custAddress, custIdentityNo, custNotes, custStatus, isLegacyTransfer, legacyCustomerNo, legacyPurchaseDate, legacyNoteNo, legacyBookNo, legacyPageNo, legacySequenceNo, legacyPrincipalAmount, legacyPaidAmount, legacyIssueDate, legacyDueDate, legacyUsePurchaseAsDue, legacyGuarantorName, legacyGuarantorPhone, legacyDescription, legacyLastPaymentDate, paymentPlanType, equalPlanData, customInstallmentData, createNote, noteAmount, noteIssueDate, noteDueDate, noteBookNo, notePageNo, noteSequenceNo, noteGuarantorName, noteGuarantorPhone, noteDescription, noteStatus, isOverdueNote };
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
    }, 400);
    return () => clearTimeout(saveTimer.current);
  }, [isOpen, isDirty, custFullName, custPhone, custSecondPhone, custCustomerNo, custCity, custDistrict, custNeighborhood, custAddress, custIdentityNo, custNotes, custStatus, isLegacyTransfer, legacyCustomerNo, legacyPurchaseDate, legacyNoteNo, legacyBookNo, legacyPageNo, legacySequenceNo, legacyPrincipalAmount, legacyPaidAmount, legacyIssueDate, legacyDueDate, legacyUsePurchaseAsDue, legacyGuarantorName, legacyGuarantorPhone, legacyDescription, legacyLastPaymentDate, paymentPlanType, equalPlanData, customInstallmentData, createNote, noteAmount, noteIssueDate, noteDueDate, noteBookNo, notePageNo, noteSequenceNo, noteGuarantorName, noteGuarantorPhone, noteDescription, noteStatus, isOverdueNote]);

  // İşletmenin kayıtlı il/ilçesini varsayılan öneri olarak doldur — kullanıcı
  // henüz bir şey girmemişse (taslak/manuel giriş varsa dokunulmaz).
  useEffect(() => {
    if (!isOpen) return;
    setCustCity((prev) => prev || businessCity || '');
    setCustDistrict((prev) => prev || businessDistrict || '');
  }, [isOpen, businessCity, businessDistrict]);

  useEffect(() => {
    if (!isOpen) { draftChecked.current = false; return; }
    const timer = setTimeout(() => {
      if (draftChecked.current) return;
      const saved = sessionStorage.getItem(DRAFT_KEY);
      if (!saved) return;
      try {
        const draft = JSON.parse(saved);
        const hasData = !!(draft.custFullName || draft.custPhone || draft.isLegacyTransfer || draft.createNote);
        if (hasData) setShowDraftPrompt(true);
      } catch { /* ignore */ }
      draftChecked.current = true;
    }, 100);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => { if (!isOpen || !isDirty) return; const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; }; window.addEventListener('beforeunload', handler); return () => window.removeEventListener('beforeunload', handler); }, [isOpen, isDirty]);

  useEffect(() => {
    if (!isOpen) return;
    window.history.pushState({ modal: 'customer-form', ts: Date.now() }, '');
    const handler = () => { if (isDirty) { window.history.pushState({ modal: 'customer-form', ts: Date.now() }, ''); setShowExitConfirm(true); } else { sessionStorage.removeItem(DRAFT_KEY); resetForm(); onClose(); } };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [isOpen, isDirty, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); if (isDirty) setShowExitConfirm(true); else { sessionStorage.removeItem(DRAFT_KEY); resetForm(); onClose(); } } };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [isOpen, isDirty, onClose]);

  function clearDraft() { sessionStorage.removeItem(DRAFT_KEY); }

  function validatePhone(phone: string): string {
    const trimmed = phone.trim();
    if (!trimmed) return 'Telefon numarası zorunludur.';
    // Rakam olmayanları temizle, baştaki 0'ı koru (normalizasyon ayrı yapılır)
    const digitsOnly = trimmed.replace(/[^0-9]/g, '');
    if (digitsOnly.length < 10) return 'Telefon numarasını eksiksiz girin.';
    // Türkiye mobil: 05XX XXX XX XX formatı (11 haneli, 0 ile başlar)
    if (!/^0?5\d{9}$/.test(digitsOnly)) return 'Geçerli bir Türkiye telefon numarası giriniz (5XX XXX XX XX).';
    return '';
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};
    if (!custFullName.trim()) errors.full_name = 'Ad Soyad zorunludur.';
    const phoneErr = validatePhone(custPhone);
    if (phoneErr) errors.phone = phoneErr;
    if (custSecondPhone.trim()) { const secondPhoneErr = validatePhone(custSecondPhone); if (secondPhoneErr) errors.second_phone = secondPhoneErr; }
    setCustErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function validateNoteForm(): boolean {
    const errs: Record<string, string> = {};
    if (!noteAmount || parseFloat(noteAmount) <= 0) errs.note_amount = 'Senet tutarı zorunludur.';
    if (!noteIssueDate) errs.note_issue_date = 'Düzenleme tarihi zorunludur.';
    if (!noteDueDate) errs.note_due_date = 'Vade tarihi zorunludur.';
    if (!noteBookNo.trim()) errs.note_book_no = 'Senet kitabı no zorunludur.';
    if (!notePageNo.trim()) errs.note_page_no = 'Senet sayfa no zorunludur.';
    setCustErrors(prev => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  }

  function validateLegacyForm(): boolean {
    const errs: Record<string, string> = {};

    // Common validations
    if (!legacyPurchaseDate) errs.legacy_purchase_date = 'Alışveriş tarihi zorunludur.';
    if (legacyPurchaseDate && legacyPurchaseDate > new Date().toISOString().split('T')[0]) errs.legacy_purchase_date = 'Gelecek tarihli alışveriş kaydı oluşturulamaz.';

    if (paymentPlanType === 'single_note') {
      if (!legacyBookNo.trim()) errs.legacy_book_no = 'Senet kitabı no zorunludur.';
      if (!legacyPageNo.trim()) errs.legacy_page_no = 'Senet sayfa no zorunludur.';
      if (!isOverdueNote && (!legacyPrincipalAmount || parseFloat(legacyPrincipalAmount) <= 0)) errs.legacy_principal = 'Senet toplam tutarı zorunludur.';
    }

    if (paymentPlanType === 'equal_installments') {
      if (!equalPlanData.totalSaleAmount || parseFloat(equalPlanData.totalSaleAmount) <= 0) errs.equal_totalSaleAmount = 'Toplam satış tutarı zorunludur.';
      if (!equalPlanData.totalInstallmentCount || parseInt(equalPlanData.totalInstallmentCount, 10) <= 0) errs.equal_totalInstallmentCount = 'Toplam taksit sayısı zorunludur.';
      if (!equalPlanData.monthlyInstallmentAmount || parseFloat(equalPlanData.monthlyInstallmentAmount) <= 0) errs.equal_monthlyInstallmentAmount = 'Aylık taksit tutarı zorunludur.';
      if (!equalPlanData.firstInstallmentDate) errs.equal_firstInstallmentDate = 'İlk taksit tarihi zorunludur.';
      const paidCount = parseInt(equalPlanData.paidInstallmentCount, 10) || 0;
      const totalCount = parseInt(equalPlanData.totalInstallmentCount, 10) || 0;
      if (paidCount > totalCount) errs.equal_paidInstallmentCount = 'Ödenmiş taksit sayısı toplam taksit sayısından fazla olamaz.';
      // Senet bilgileri: kitap ve sayfa zorunlu
      if (!legacyBookNo.trim()) errs.legacy_book_no = 'Senet kitabı no zorunludur.';
      if (!legacyPageNo.trim()) errs.legacy_page_no = 'Senet sayfa no zorunludur.';
    }

    if (paymentPlanType === 'custom_installments') {
      if (customInstallmentData.length === 0) errs.plan_validation = 'En az bir taksit girişi yapmalısınız.';
      else {
        const totalAmount = customInstallmentData.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const totalPaid = customInstallmentData.reduce((s, r) => s + (parseFloat(r.paid_amount) || 0), 0);
        const totalRemaining = Math.max(0, totalAmount - totalPaid);
        const legacyPrincipal = parseFloat(legacyPrincipalAmount) || 0;
        // Yalnızca kullanıcı ayrıca devredilen borç tutarı girdiyse doğrula
        if (legacyPrincipal > 0) {
          const legacyPaid = parseFloat(legacyPaidAmount) || 0;
          const legacyRemaining = Math.max(0, legacyPrincipal - legacyPaid);
          if (Math.abs(totalRemaining - legacyRemaining) > 0.01) {
            errs.plan_validation = `Taksitlerin toplam kalan tutarı (${totalRemaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺), devredilen kalan borca (${legacyRemaining.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺) eşit olmak zorundadır!`;
          }
        }
        // Kullanıcı borç tutarı girmediyse (0 veya boş) → taksit toplamını esas al, hata verme
      }
      // Senet bilgileri: kitap ve sayfa zorunlu
      if (!legacyBookNo.trim()) errs.legacy_book_no = 'Senet kitabı no zorunludur.';
      if (!legacyPageNo.trim()) errs.legacy_page_no = 'Senet sayfa no zorunludur.';
    }

    setCustErrors(prev => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  }

  const legacyRemainingAmount = useMemo(() => {
    if (paymentPlanType === 'equal_installments') {
      const totalSale = parseFloat(equalPlanData.totalSaleAmount) || 0;
      const previouslyPaid = parseFloat(equalPlanData.previouslyPaid) || 0;
      return Math.max(0, totalSale - previouslyPaid);
    }
    if (paymentPlanType === 'custom_installments') {
      const totalAmount = customInstallmentData.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
      const totalPaid = customInstallmentData.reduce((s, r) => s + (parseFloat(r.paid_amount) || 0), 0);
      return Math.max(0, totalAmount - totalPaid);
    }
    // single_note
    const principal = parseFloat(legacyPrincipalAmount) || 0;
    const paid = parseFloat(legacyPaidAmount) || 0;
    return Math.max(0, principal - paid);
  }, [legacyPrincipalAmount, legacyPaidAmount, paymentPlanType, equalPlanData, customInstallmentData]);

  const effectiveLegacyDueDate = legacyUsePurchaseAsDue ? legacyPurchaseDate : legacyDueDate;

  function handleClose() { if (isSaving || submittingRef.current) return; if (isDirty) { setShowExitConfirm(true); return; } handleForceClose(); }
  function handleForceClose() { clearDraft(); resetForm(); setShowExitConfirm(false); setShowDraftPrompt(false); onClose(); }

  function handleDraftContinue() {
    const saved = sessionStorage.getItem(DRAFT_KEY);
    if (saved) {
      try {
        const d = JSON.parse(saved);
        if (d.custFullName !== undefined) setCustFullName(d.custFullName);
        if (d.custPhone !== undefined) setCustPhone(d.custPhone);
        if (d.custSecondPhone !== undefined) setCustSecondPhone(d.custSecondPhone);
        if (d.custCustomerNo !== undefined) setCustCustomerNo(d.custCustomerNo);
        if (d.custCity !== undefined) setCustCity(d.custCity);
        if (d.custDistrict !== undefined) setCustDistrict(d.custDistrict);
        if (d.custNeighborhood !== undefined) setCustNeighborhood(d.custNeighborhood);
        if (d.custAddress !== undefined) setCustAddress(d.custAddress);
        if (d.custIdentityNo !== undefined) setCustIdentityNo(d.custIdentityNo);
        if (d.custNotes !== undefined) setCustNotes(d.custNotes);
        if (d.custStatus !== undefined) setCustStatus(d.custStatus);
        if (d.isLegacyTransfer !== undefined) setIsLegacyTransfer(d.isLegacyTransfer);
        if (d.legacyCustomerNo !== undefined) setLegacyCustomerNo(d.legacyCustomerNo);
        if (d.legacyPurchaseDate !== undefined) setLegacyPurchaseDate(d.legacyPurchaseDate);
        if (d.legacyNoteNo !== undefined) setLegacyNoteNo(d.legacyNoteNo);
        if (d.legacyBookNo !== undefined) setLegacyBookNo(d.legacyBookNo);
        if (d.legacyPageNo !== undefined) setLegacyPageNo(d.legacyPageNo);
        if (d.legacySequenceNo !== undefined) setLegacySequenceNo(d.legacySequenceNo);
        if (d.legacyPrincipalAmount !== undefined) setLegacyPrincipalAmount(d.legacyPrincipalAmount);
        if (d.legacyPaidAmount !== undefined) setLegacyPaidAmount(d.legacyPaidAmount);
        if (d.legacyIssueDate !== undefined) setLegacyIssueDate(d.legacyIssueDate);
        if (d.legacyDueDate !== undefined) setLegacyDueDate(d.legacyDueDate);
        if (d.legacyUsePurchaseAsDue !== undefined) setLegacyUsePurchaseAsDue(d.legacyUsePurchaseAsDue);
        if (d.legacyGuarantorName !== undefined) setLegacyGuarantorName(d.legacyGuarantorName);
        if (d.legacyGuarantorPhone !== undefined) setLegacyGuarantorPhone(d.legacyGuarantorPhone);
        if (d.legacyDescription !== undefined) setLegacyDescription(d.legacyDescription);
        if (d.legacyLastPaymentDate !== undefined) setLegacyLastPaymentDate(d.legacyLastPaymentDate);
        if (d.paymentPlanType !== undefined) setPaymentPlanType(d.paymentPlanType);
        if (d.equalPlanData !== undefined) setEqualPlanData(d.equalPlanData);
        if (d.customInstallmentData !== undefined) setCustomInstallmentData(d.customInstallmentData);
        if (d.createNote !== undefined) setCreateNote(d.createNote);
        if (d.noteAmount !== undefined) setNoteAmount(d.noteAmount);
        if (d.noteIssueDate !== undefined) setNoteIssueDate(d.noteIssueDate);
        if (d.noteDueDate !== undefined) setNoteDueDate(d.noteDueDate);
        if (d.noteBookNo !== undefined) setNoteBookNo(d.noteBookNo);
        if (d.notePageNo !== undefined) setNotePageNo(d.notePageNo);
        if (d.noteSequenceNo !== undefined) setNoteSequenceNo(d.noteSequenceNo);
        if (d.noteGuarantorName !== undefined) setNoteGuarantorName(d.noteGuarantorName);
        if (d.noteGuarantorPhone !== undefined) setNoteGuarantorPhone(d.noteGuarantorPhone);
        if (d.noteDescription !== undefined) setNoteDescription(d.noteDescription);
        if (d.noteStatus !== undefined) setNoteStatus(d.noteStatus);
        if (d.isOverdueNote !== undefined) setIsOverdueNote(d.isOverdueNote);
      } catch { /* ignore */ }
    }
    setShowDraftPrompt(false);
  }

  function handleDraftDiscard() { clearDraft(); setShowDraftPrompt(false); }

  // ── Silinmiş müşteri dialog handler'ları ──
  async function handleRestoreDeletedCustomer() {
    if (!deletedCustomerInfo?.id) return;
    setIsSaving(true);
    try {
      const res = await restoreDeletedCustomer(deletedCustomerInfo.id);
      if (res.error) {
        showToast(res.error, 'error');
        setIsSaving(false);
        return;
      }
      clearDraft();
      showToast('Silinmiş müşteri başarıyla geri yüklendi.', 'success');
      if (onCreated) onCreated(deletedCustomerInfo.id);
      triggerRefresh();
      resetForm();
      setShowDeletedCustomerDialog(false);
      setDeletedCustomerInfo(null);
      onClose();
    } catch {
      showToast('Geri yükleme sırasında beklenmeyen bir hata oluştu.', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleCreateNewDespiteDeleted() {
    setShowDeletedCustomerDialog(false);
    setDeletedCustomerInfo(null);
    // force_create=true ile tekrar dene — silinmiş müşteriyi yoksay
    submittingRef.current = true;
    setIsSaving(true);

    try {
      const createdRes = await createCustomerAtomic({
        full_name: custFullName.trim(),
        phone: custPhone.trim(),
        customer_no: custCustomerNo.trim() || undefined,
        second_phone: custSecondPhone.trim() || undefined,
        address: custAddress.trim() || undefined,
        city: custCity.trim(),
        district: custDistrict.trim(),
        neighborhood: custNeighborhood.trim(),
        identity_number: custIdentityNo.trim() || undefined,
        notes: custNotes.trim() || undefined,
        status: custStatus,
      }, true);

      if (createdRes.error || !createdRes.data) {
        showToast(createdRes.error || 'Müşteri oluşturulamadı.', 'error');
        if (createdRes.error && createdRes.error.includes('telefon')) {
          setCustErrors(prev => ({ ...prev, phone: createdRes.error! }));
        }
        setIsSaving(false);
        submittingRef.current = false;
        return;
      }

      const created = createdRes.data;
      clearDraft();
      showToast('Müşteri başarıyla eklendi.', 'success');
      if (onCreated) onCreated(created.id);
      triggerRefresh();
      resetForm();
      onClose();
    } catch {
      showToast('Kayıt sırasında beklenmeyen bir hata oluştu.', 'error');
      setIsSaving(false);
      submittingRef.current = false;
    }
  }

  function handleCancelDeletedDialog() {
    setShowDeletedCustomerDialog(false);
    setDeletedCustomerInfo(null);
    // Formu kapatma, kullanıcı telefonu değiştirip tekrar deneyebilir
  }

  async function handleSave() {
    // ── KORUMA 1: Ref + state ile anlık çift tıklama engelleme ──
    if (isSaving || submittingRef.current) return;
    submittingRef.current = true;
    setIsSaving(true);
    setCustTouched(true);

    try {
      // ── ADIM 1: Form validasyonu (başarısızsa HİÇBİR şey yapma) ──
      if (!validateForm()) {
        setIsSaving(false);
        submittingRef.current = false;
        return;
      }

      // ── Legacy transfer yolu ──
      if (isLegacyTransfer) {
        if (!validateLegacyForm()) {
          setIsSaving(false);
          submittingRef.current = false;
          return;
        }

        // Legacy transfer: customer_no boş bırakılırsa RPC kendi halleder
        let finalCustomerNo = custCustomerNo.trim() || legacyCustomerNo.trim() || undefined;
        const customerPayload = {
          customer_no: finalCustomerNo,
          full_name: custFullName.trim(),
          phone: custPhone.trim(),
          second_phone: custSecondPhone.trim(),
          address: custAddress.trim(),
          city: custCity.trim(),
          district: custDistrict.trim(),
          neighborhood: custNeighborhood.trim(),
          identity_number: custIdentityNo.trim(),
          notes: custNotes.trim(),
          status: isOverdueNote ? 'Borçlu' : (legacyRemainingAmount > 0 ? 'Borçlu' : 'Borcu Yok'),
        };

        const remaining = legacyRemainingAmount;

        if (paymentPlanType === 'single_note') {
          const principal = isOverdueNote ? 0 : (parseFloat(legacyPrincipalAmount) || 0);
          const paid = isOverdueNote ? 0 : (parseFloat(legacyPaidAmount) || 0);

          const result = await createLegacyCustomerWithNote({
            customer: {
              customer_no: finalCustomerNo,
              full_name: custFullName.trim(),
              phone: custPhone.trim(),
              second_phone: custSecondPhone.trim(),
              address: custAddress.trim(),
              city: custCity.trim(),
              district: custDistrict.trim(),
              neighborhood: custNeighborhood.trim(),
              identity_number: custIdentityNo.trim(),
              notes: custNotes.trim(),
              status: isOverdueNote ? 'Borçlu' : (remaining > 0 ? 'Borçlu' : 'Borcu Yok'),
            },
            note: {
              note_no: legacyNoteNo.trim() || null,
              legacy_note_no: legacyNoteNo.trim() || null,
              purchase_date: legacyPurchaseDate,
              amount: principal,
              paid_amount: paid,
              issue_date: legacyIssueDate || legacyPurchaseDate,
              due_date: effectiveLegacyDueDate,
              note_book_no: normalizeBookNo(legacyBookNo),
              note_page_no: legacyPageNo.trim(),
              note_sequence_no: legacySequenceNo.trim(),
              guarantor_name: legacyGuarantorName.trim(),
              guarantor_phone: legacyGuarantorPhone.trim(),
              description: legacyDescription.trim(),
              status: isOverdueNote ? 'Günhükmü' : undefined,
            },
            legacyPayment: !isOverdueNote && paid > 0 ? { amount: paid, payment_date: legacyLastPaymentDate || legacyPurchaseDate } : null,
          });

          if (result.error) {
            // ── Debug: legacy path hatasının tam kaynağını console'a yaz ──
            console.debug('[CustomerForm Legacy] Kayıt hatası:', {
              error: result.error,
              path: 'createLegacyCustomerWithNote',
            });
            showToast(result.error, 'error');
            setIsSaving(false);
            submittingRef.current = false;
            return;
          }
          clearDraft();
          showToast('Müşteri ve eski sistem devir kaydı başarıyla oluşturuldu.', 'success');
          if (onCreated && result.data?.customer) onCreated(result.data.customer.id);
          triggerRefresh(); resetForm(); onClose();
          return;
        }

        if (paymentPlanType === 'equal_installments') {
          const result = await createLegacyCustomerWithPlan({
            customer: {
              customer_no: finalCustomerNo,
              full_name: custFullName.trim(),
              phone: custPhone.trim(),
              second_phone: custSecondPhone.trim(),
              address: custAddress.trim(),
              city: custCity.trim(),
              district: custDistrict.trim(),
              neighborhood: custNeighborhood.trim(),
              identity_number: custIdentityNo.trim(),
              notes: custNotes.trim(),
              status: remaining > 0 ? 'Borçlu' : 'Borcu Yok',
            },
            legacySale: {
              purchase_date: legacyPurchaseDate,
              total_amount: parseFloat(equalPlanData.totalSaleAmount) || 0,
              paid_amount: parseFloat(equalPlanData.previouslyPaid) || 0,
              remaining_amount: remaining,
              payment_type: 'Senet',
              notes: legacyDescription.trim(),
            },
            planType: 'equal',
            equalPlan: equalPlanData,
            customInstallments: null,
            noteData: {
              note_no: legacyNoteNo.trim() || null,
              note_book_no: normalizeBookNo(legacyBookNo),
              note_page_no: legacyPageNo.trim(),
              note_sequence_no: legacySequenceNo.trim(),
              issue_date: legacyIssueDate || legacyPurchaseDate,
              due_date: effectiveLegacyDueDate || legacyPurchaseDate,
              guarantor_name: legacyGuarantorName.trim(),
              guarantor_phone: legacyGuarantorPhone.trim(),
              description: legacyDescription.trim(),
            },
          });

          if (result.error) {
            console.debug('[CustomerForm Legacy Equal] Kayıt hatası:', {
              error: result.error,
              path: 'createLegacyCustomerWithPlan-equal',
            });
            showToast(result.error, 'error');
            setIsSaving(false);
            submittingRef.current = false;
            return;
          }
          clearDraft();
          if (result.data?.note) {
            showToast('Müşteri ve eşit taksitli devir kaydı başarıyla oluşturuldu. Senet de oluşturuldu.', 'success');
          } else {
            showToast('Müşteri ve eşit taksitli devir kaydı oluşturuldu ancak senet oluşturulamadı! Lütfen senetler sayfasından manuel ekleyin.', 'warning');
          }
          if (onCreated && result.data?.customer) onCreated(result.data.customer.id);
          triggerRefresh(); resetForm(); onClose();
          return;
        }

        if (paymentPlanType === 'custom_installments') {
          const result = await createLegacyCustomerWithPlan({
            customer: {
              customer_no: finalCustomerNo,
              full_name: custFullName.trim(),
              phone: custPhone.trim(),
              second_phone: custSecondPhone.trim(),
              address: custAddress.trim(),
              city: custCity.trim(),
              district: custDistrict.trim(),
              neighborhood: custNeighborhood.trim(),
              identity_number: custIdentityNo.trim(),
              notes: custNotes.trim(),
              status: remaining > 0 ? 'Borçlu' : 'Borcu Yok',
            },
            legacySale: {
              purchase_date: legacyPurchaseDate,
              total_amount: customInstallmentData.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
              paid_amount: customInstallmentData.reduce((s, r) => s + (parseFloat(r.paid_amount) || 0), 0),
              remaining_amount: remaining,
              payment_type: 'Senet',
              notes: legacyDescription.trim(),
            },
            planType: 'custom',
            equalPlan: null,
            customInstallments: customInstallmentData,
            noteData: {
              note_no: legacyNoteNo.trim() || null,
              note_book_no: normalizeBookNo(legacyBookNo),
              note_page_no: legacyPageNo.trim(),
              note_sequence_no: legacySequenceNo.trim(),
              issue_date: legacyIssueDate || legacyPurchaseDate,
              due_date: effectiveLegacyDueDate || legacyPurchaseDate,
              guarantor_name: legacyGuarantorName.trim(),
              guarantor_phone: legacyGuarantorPhone.trim(),
              description: legacyDescription.trim(),
            },
          });

          if (result.error) {
            console.debug('[CustomerForm Legacy Custom] Kayıt hatası:', {
              error: result.error,
              path: 'createLegacyCustomerWithPlan-custom',
            });
            showToast(result.error, 'error');
            setIsSaving(false);
            submittingRef.current = false;
            return;
          }
          clearDraft();
          if (result.data?.note) {
            showToast('Müşteri ve özel taksitli devir kaydı başarıyla oluşturuldu. Senet de oluşturuldu.', 'success');
          } else {
            showToast('Müşteri ve özel taksitli devir kaydı oluşturuldu ancak senet oluşturulamadı! Lütfen senetler sayfasından manuel ekleyin.', 'warning');
          }
          if (onCreated && result.data?.customer) onCreated(result.data.customer.id);
          triggerRefresh(); resetForm(); onClose();
          return;
        }
      }

      // ── Normal müşteri ekleme yolu (ATOMİK RPC) ──
      if (createNote && !validateNoteForm()) {
        setIsSaving(false);
        submittingRef.current = false;
        return;
      }

      // Tüm doğrulama, mükerrer kontrolü, müşteri no oluşturma ve insert
      // TEK atomik RPC çağrısında yapılır — race condition imkansız
      const createdRes = await createCustomerAtomic({
        full_name: custFullName.trim(),
        phone: custPhone.trim(),
        customer_no: custCustomerNo.trim() || undefined,
        second_phone: custSecondPhone.trim() || undefined,
        address: custAddress.trim() || undefined,
        city: custCity.trim(),
        district: custDistrict.trim(),
        neighborhood: custNeighborhood.trim(),
        identity_number: custIdentityNo.trim() || undefined,
        notes: custNotes.trim() || undefined,
        status: custStatus,
      });

      if (createdRes.error || !createdRes.data) {
        // ── Debug: hatanın tam kaynağını console'a yaz ──
        console.debug('[CustomerForm] Kayıt hatası:', {
          errorCode: createdRes.errorCode,
          error: createdRes.error,
          debugInfo: createdRes.debugInfo,
          inputPhone: custPhone.trim(),
        });

        // ── Silinmiş müşteri tespit edildi mi? ──
        if (createdRes.deletedCustomer) {
          setDeletedCustomerInfo(createdRes.deletedCustomer);
          setShowDeletedCustomerDialog(true);
          setIsSaving(false);
          submittingRef.current = false;
          return;
        }

        // ── Hata koduna göre doğru alanı işaretle ──
        const errorCode = createdRes.errorCode;
        if (errorCode === 'DUPLICATE_PHONE_ACTIVE' || errorCode === 'DUPLICATE_PHONE' || errorCode === 'INVALID_PHONE' || errorCode === 'MISSING_PHONE') {
          // Gerçek telefon hatası
          showToast(createdRes.error || 'Telefon numarası hatası.', 'error');
          setCustErrors(prev => ({ ...prev, phone: createdRes.error! }));
        } else if (errorCode === 'DUPLICATE_CUSTOMER_NO') {
          // Müşteri numarası çakışması — telefonla alakası yok!
          showToast(createdRes.error || 'Müşteri numarası zaten kullanılıyor.', 'error');
          setCustErrors(prev => ({ ...prev, customer_no: createdRes.error! }));
        } else if (errorCode === 'MISSING_NAME') {
          setCustErrors(prev => ({ ...prev, full_name: createdRes.error! }));
        } else if (errorCode === 'SERVER_ERROR' || errorCode === 'UNIQUE_VIOLATION') {
          // Sunucu hatası — teknik detay göster ama telefon hatası deme!
          showToast('Sunucu kaynaklı bir hata oluştu. Lütfen tekrar deneyin.', 'error');
          console.error('[CustomerForm] Sunucu hatası:', createdRes.debugInfo);
        } else {
          // Diğer tüm hatalar
          showToast(createdRes.error || 'Müşteri oluşturulamadı.', 'error');
        }
        // Formu kapatma, verileri koru!
        setIsSaving(false);
        submittingRef.current = false;
        return;
      }

      const created = createdRes.data;

      // ── Senet oluştur (opsiyonel) ──
      if (createNote) {
        try {
          const noteNoRes = await generateNoteNo();
          const noteNo = noteNoRes.data || `SNT-${new Date().getFullYear()}-001`;
          await createPromissoryNote({
            note_no: noteNo,
            customer_id: created.id,
            customer_name: created.full_name,
            customer_phone: created.phone,
            amount: parseFloat(noteAmount),
            issue_date: noteIssueDate,
            due_date: noteDueDate,
            note_book_no: normalizeBookNo(noteBookNo),
            note_page_no: notePageNo.trim(),
            note_sequence_no: noteSequenceNo.trim(),
            guarantor_name: noteGuarantorName.trim(),
            guarantor_phone: noteGuarantorPhone.trim(),
            description: noteDescription.trim(),
            status: noteStatus,
            sale_id: null,
            sale_no: null,
          });
          showToast('Müşteri ve senet kaydı başarıyla oluşturuldu.', 'success');
        } catch {
          showToast('Müşteri oluşturuldu ancak senet kaydı başarısız oldu.', 'warning');
        }
      } else {
        showToast('Müşteri başarıyla eklendi.', 'success');
      }

      // ── BAŞARILI → temizle ve kapat ──
      clearDraft();
      if (onCreated) onCreated(created.id);
      triggerRefresh();
      resetForm();
      onClose();
    } catch {
      showToast('Kayıt sırasında beklenmeyen bir hata oluştu.', 'error');
      setIsSaving(false);
      submittingRef.current = false;
      // Formu kapatma, verileri koru
    }
  }

  const [autoNoteNo, setAutoNoteNo] = useState('');

  useEffect(() => {
    if (isOpen) {
      generateNoteNo().then(r => setAutoNoteNo(r.data || ''));
    }
  }, [isOpen]);

  // Header content
  const headerContent = (
    <div className="flex items-center justify-between px-5 py-3.5">
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
          <i className="ri-user-add-line text-background-50 text-sm"></i>
        </span>
        <h3 className="text-base font-semibold text-foreground-900">Yeni Müşteri Ekle</h3>
      </div>
      <button onClick={handleClose} disabled={isSaving || submittingRef.current} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-pointer disabled:opacity-50">
        <i className="ri-close-line"></i>
      </button>
    </div>
  );

  // Footer content
  const footerContent = (
    <div className="flex items-center justify-end gap-3 px-5 py-3">
      <button onClick={handleClose} disabled={isSaving || submittingRef.current} className="btn-outline cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm">İptal</button>
      <button onClick={handleSave} disabled={isSaving || submittingRef.current} className="btn-primary flex items-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60 text-sm flex-1 lg:flex-none justify-center">
        {isSaving ? <><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</> : <><i className="ri-check-line"></i>{isLegacyTransfer ? 'Devir Kaydını Tamamla' : 'Müşteriyi Kaydet'}</>}
      </button>
    </div>
  );

  // Overlay content
  const overlayNode = showDeletedCustomerDialog && deletedCustomerInfo ? (
    <div className="bg-background-50 rounded-xl border border-background-200 shadow-xl p-6 mx-6 max-w-sm w-full">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><i className="ri-user-received-line text-amber-600 text-lg"></i></span>
        <div>
          <h4 className="text-sm font-semibold text-foreground-900">Silinmiş Müşteri Kaydı</h4>
          <p className="text-xs text-foreground-500 mt-0.5">Bu telefon numarasına ait silinmiş bir müşteri kaydı bulundu.</p>
        </div>
      </div>
      {deletedCustomerInfo.full_name && (
        <p className="text-xs text-foreground-600 mb-4 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
          <span className="font-medium">{deletedCustomerInfo.full_name}</span>
        </p>
      )}
      <div className="flex flex-col gap-2">
        <button
          onClick={handleRestoreDeletedCustomer}
          disabled={isSaving}
          className="w-full py-2.5 px-3 text-sm rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 cursor-pointer whitespace-nowrap disabled:opacity-50 font-medium"
        >
          <i className="ri-arrow-go-back-line mr-1.5"></i>Eski Müşteriyi Geri Yükle
        </button>
        <button
          onClick={handleCreateNewDespiteDeleted}
          disabled={isSaving}
          className="w-full py-2.5 px-3 text-sm rounded-lg border border-background-200 text-foreground-700 hover:bg-background-100 cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          <i className="ri-user-add-line mr-1.5"></i>Yeni Müşteri Olarak Oluştur
        </button>
        <button
          onClick={handleCancelDeletedDialog}
          disabled={isSaving}
          className="w-full py-2 px-3 text-sm rounded-lg text-foreground-400 hover:text-foreground-600 cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          İptal
        </button>
      </div>
    </div>
  ) : showDraftPrompt ? (
    <div className="bg-background-50 rounded-xl border border-background-200 shadow-xl p-6 mx-6 max-w-sm w-full">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><i className="ri-file-history-line text-amber-600 text-lg"></i></span>
        <div><h4 className="text-sm font-semibold text-foreground-900">Kaydedilmemiş Taslak</h4><p className="text-xs text-foreground-500">Kaydedilmemiş bir müşteri taslağı bulundu.</p></div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleDraftDiscard} className="flex-1 py-2 px-3 text-sm rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap">Taslağı Sil</button>
        <button onClick={handleDraftContinue} className="flex-1 py-2 px-3 text-sm rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap">Taslağı Devam Ettir</button>
      </div>
    </div>
  ) : showExitConfirm ? (
    <div className="bg-background-50 rounded-xl border border-background-200 shadow-xl p-6 mx-6 max-w-sm w-full">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0"><i className="ri-alert-line text-red-500 text-lg"></i></span>
        <div><h4 className="text-sm font-semibold text-foreground-900">Kaydedilmemiş Değişiklikler</h4><p className="text-xs text-foreground-500 mt-0.5">Değişiklikler kaybolacak.</p></div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleForceClose} disabled={isSaving || submittingRef.current} className="flex-1 py-2 px-3 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap disabled:opacity-50">Değişiklikleri Sil ve Çık</button>
        <button onClick={() => setShowExitConfirm(false)} className="flex-1 py-2 px-3 text-sm rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap">Forma Geri Dön</button>
      </div>
    </div>
  ) : null;

  return (
    <ResponsiveFormModal
      isOpen={isOpen}
      onClose={handleClose}
      zIndex={zIndex}
      header={headerContent}
      footer={footerContent}
      overlayContent={overlayNode}
    >
      <div className="px-5 py-4 space-y-4">
        {/* Legacy transfer toggle */}
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-archive-line text-background-50 text-sm"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-amber-800">Eski Sistemden Devir</p>
                <p className="text-xs text-amber-600">Eski sistemdeki müşteri ve borç kaydını aktar</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={isLegacyTransfer} onChange={e => { setIsLegacyTransfer(e.target.checked); if (e.target.checked) setCreateNote(false); setCustErrors({}); }} className="sr-only peer" />
              <div className="w-9 h-5 bg-background-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-background-50 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:border-background-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
            </label>
          </div>
        </div>

        {/* Main customer form */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className={labelClass}>Ad Soyad <span className="text-red-400">*</span></label>
            <input type="text" value={custFullName} onChange={e => { setCustFullName(capitalizeWords(e.target.value)); setCustErrors(prev => ({ ...prev, full_name: '' })); }} className={custErrors.full_name ? inputErrorClass : inputClass} placeholder="Ad Soyad" />
            {custErrors.full_name && <p className={errorTextClass}>{custErrors.full_name}</p>}
          </div>
          <div>
            <label className={labelClass}>Müşteri No</label>
            <input type="text" value={custCustomerNo} onChange={e => { setCustCustomerNo(e.target.value); setCustErrors(prev => ({ ...prev, customer_no: '' })); }} className={custErrors.customer_no ? inputErrorClass : inputClass} placeholder="Boş bırakılırsa otomatik" />
            <p className={helperTextClass}>Boş bırakılırsa otomatik atanır</p>
            {custErrors.customer_no && <p className={errorTextClass}>{custErrors.customer_no}</p>}
          </div>
          <div>
            <label className={labelClass}>Müşteri Durumu</label>
            <select value={custStatus} onChange={e => setCustStatus(e.target.value as CustomerStatus)} className={selectClass}>
              <option value="Aktif">Aktif</option><option value="Borçlu">Borçlu</option><option value="Borcu Yok">Borcu Yok</option><option value="Kara Liste">Kara Liste</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>1. Telefon <span className="text-red-400">*</span></label>
            <input type="text" value={custPhone} onChange={e => { setCustPhone(digitsOnly(e.target.value)); setCustErrors(prev => ({ ...prev, phone: '' })); }} className={custErrors.phone ? inputErrorClass : inputClass} placeholder="05XX XXX XX XX" />
            {custErrors.phone && <p className={errorTextClass}>{custErrors.phone}</p>}
          </div>
          <div>
            <label className={labelClass}>2. Telefon</label>
            <input type="text" value={custSecondPhone} onChange={e => { setCustSecondPhone(digitsOnly(e.target.value)); setCustErrors(prev => ({ ...prev, second_phone: '' })); }} className={custErrors.second_phone ? inputErrorClass : inputClass} placeholder="05XX XXX XX XX" />
            {custErrors.second_phone && <p className={errorTextClass}>{custErrors.second_phone}</p>}
          </div>
          <div className="sm:col-span-2">
            <CityDistrictSelect
              city={custCity}
              district={custDistrict}
              neighborhood={custNeighborhood}
              onCityChange={setCustCity}
              onDistrictChange={setCustDistrict}
              onNeighborhoodChange={setCustNeighborhood}
              selectClassName={selectClass}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Adres</label>
            <textarea value={custAddress} onChange={e => setCustAddress(e.target.value)} maxLength={500} className={`${inputClass} resize-none h-20`} placeholder="Açık adres..."></textarea>
          </div>
          <div>
            <label className={labelClass}>TC Kimlik No</label>
            <input type="text" value={custIdentityNo} onChange={e => setCustIdentityNo(e.target.value)} className={inputClass} placeholder="11 haneli" maxLength={11} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>Müşteri Notu</label>
            <textarea value={custNotes} onChange={e => setCustNotes(e.target.value)} maxLength={500} className={`${inputClass} resize-none h-20`} placeholder="Müşteri ile ilgili notlar..."></textarea>
          </div>
        </div>

        {/* Legacy transfer fields */}
        {isLegacyTransfer && (
          <div className="pt-4 border-t-2 border-amber-200">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-folder-transfer-line text-background-50 text-xs"></i>
              </span>
              <h4 className="text-sm font-semibold text-amber-800">Eski Sistem / Devir Bilgileri</h4>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div><label className={labelClass}>Eski Müşteri No</label><input type="text" value={legacyCustomerNo} onChange={e => setLegacyCustomerNo(e.target.value)} className={inputClass} placeholder="Opsiyonel" /></div>
              <div><label className={labelClass}>Alışveriş Tarihi <span className="text-red-400">*</span></label><input type="date" value={legacyPurchaseDate} max={new Date().toISOString().split('T')[0]} onChange={e => { setLegacyPurchaseDate(e.target.value); setLegacyIssueDate(e.target.value); setCustErrors(prev => ({ ...prev, legacy_purchase_date: '' })); if (legacyUsePurchaseAsDue) setLegacyDueDate(e.target.value); }} className={custErrors.legacy_purchase_date ? inputErrorClass : inputClass} />{custErrors.legacy_purchase_date && <p className={errorTextClass}>{custErrors.legacy_purchase_date}</p>}</div>
              <div className="sm:col-span-2"><label className={labelClass}>Açıklama</label><textarea value={legacyDescription} onChange={e => setLegacyDescription(e.target.value)} maxLength={500} className={`${inputClass} resize-none h-20`} placeholder="Devir ile ilgili açıklama..."></textarea></div>
            </div>

            {/* Payment Plan Section */}
            <LegacyInstallmentPlan
              planType={paymentPlanType}
              setPlanType={setPaymentPlanType}
              equalPlan={equalPlanData}
              setEqualPlan={setEqualPlanData}
              customInstallments={customInstallmentData}
              setCustomInstallments={setCustomInstallmentData}
              errors={custErrors}
              setErrors={setCustErrors}
              legacyRemainingAmount={
                paymentPlanType === 'single_note'
                  ? (parseFloat(legacyPrincipalAmount) || 0) - (parseFloat(legacyPaidAmount) || 0)
                  : legacyRemainingAmount
              }
              legacyNoteNo={legacyNoteNo}
              setLegacyNoteNo={setLegacyNoteNo}
              legacyBookNo={legacyBookNo}
              setLegacyBookNo={setLegacyBookNo}
              legacyPageNo={legacyPageNo}
              setLegacyPageNo={setLegacyPageNo}
              legacySequenceNo={legacySequenceNo}
              setLegacySequenceNo={setLegacySequenceNo}
            />

            {/* Single note fields - only show when single_note plan selected */}
            {paymentPlanType === 'single_note' && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center flex-shrink-0">
                        <i className="ri-alert-line text-background-50 text-xs"></i>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-red-800">Günhükmü Senet</p>
                        <p className="text-xs text-red-600">Vadesi geçmiş, icralık senet (tutar girilmez)</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={isOverdueNote} onChange={e => { setIsOverdueNote(e.target.checked); if (e.target.checked) { setLegacyPrincipalAmount('0'); setLegacyPaidAmount('0'); } }} className="sr-only peer" />
                      <div className="w-9 h-5 bg-background-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-background-50 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:border-background-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
                    </label>
                  </div>
                </div>
                <div><label className={labelClass}>Eski Senet No</label><input type="text" value={legacyNoteNo} onChange={e => { setLegacyNoteNo(e.target.value); setCustErrors(prev => ({ ...prev, legacy_note_no: '' })); }} className={custErrors.legacy_note_no ? inputErrorClass : inputClass} placeholder="Boş bırakılırsa otomatik" /><p className={helperTextClass}>Boş bırakılırsa otomatik atanır</p>{custErrors.legacy_note_no && <p className={errorTextClass}>{custErrors.legacy_note_no}</p>}</div>
                <div><label className={labelClass}>Senet Kitabı No <span className="text-red-400">*</span></label><input type="text" value={legacyBookNo} onChange={e => { setLegacyBookNo(e.target.value); setCustErrors(prev => ({ ...prev, legacy_book_no: '' })); }} className={custErrors.legacy_book_no ? inputErrorClass : inputClass} placeholder="Örn: Kitap-1" />{custErrors.legacy_book_no && <p className={errorTextClass}>{custErrors.legacy_book_no}</p>}</div>
                <div><label className={labelClass}>Senet Sayfa No <span className="text-red-400">*</span></label><input type="text" value={legacyPageNo} onChange={e => { setLegacyPageNo(e.target.value); setCustErrors(prev => ({ ...prev, legacy_page_no: '' })); }} className={custErrors.legacy_page_no ? inputErrorClass : inputClass} placeholder="Örn: 15" />{custErrors.legacy_page_no && <p className={errorTextClass}>{custErrors.legacy_page_no}</p>}</div>
                <div><label className={labelClass}>Senet Sıra No</label><input type="text" value={legacySequenceNo} onChange={e => setLegacySequenceNo(e.target.value)} className={inputClass} placeholder="Opsiyonel" /></div>
                <div><label className={labelClass}>Senet Toplam Tutarı (₺) {!isOverdueNote && <span className="text-red-400">*</span>}</label><CurrencyInput value={legacyPrincipalAmount} onChange={v => { setLegacyPrincipalAmount(v); setCustErrors(prev => ({ ...prev, legacy_principal: '' })); }} className={custErrors.legacy_principal ? inputErrorClass : inputClass} placeholder="0" disabled={isOverdueNote} />{custErrors.legacy_principal && <p className={errorTextClass}>{custErrors.legacy_principal}</p>}</div>
                <div><label className={labelClass}>Daha Önce Ödenen (₺)</label><CurrencyInput value={legacyPaidAmount} onChange={setLegacyPaidAmount} className={inputClass} placeholder="0" disabled={isOverdueNote} /></div>
                <div><label className={labelClass}>Kalan Borç (₺)</label><div className={readonlyClass}>{legacyRemainingAmount.toLocaleString('tr-TR')} ₺</div><p className={helperTextClass}>Otomatik hesaplanır</p></div>
                <div><label className={labelClass}>Son Ödeme Tarihi</label><input type="date" value={legacyLastPaymentDate} max={new Date().toISOString().split('T')[0]} onChange={e => setLegacyLastPaymentDate(e.target.value)} className={inputClass} /><p className={helperTextClass}>Bilinmiyorsa alışveriş tarihi kullanılır</p></div>
                <div><label className={labelClass}>Senet Düzenleme Tarihi</label><input type="date" value={legacyIssueDate} onChange={e => setLegacyIssueDate(e.target.value)} className={inputClass} /><p className={helperTextClass}>Varsayılan: Alışveriş tarihi</p></div>
                <div><label className={labelClass}>Senet Vade Tarihi</label><input type="date" value={effectiveLegacyDueDate} onChange={e => setLegacyDueDate(e.target.value)} className={inputClass} disabled={legacyUsePurchaseAsDue} /></div>
                <div className="flex items-center gap-2 self-end pb-0.5"><input type="checkbox" id="usePurchaseAsDue" checked={legacyUsePurchaseAsDue} onChange={e => { setLegacyUsePurchaseAsDue(e.target.checked); if (e.target.checked) setLegacyDueDate(legacyPurchaseDate); }} className="w-4 h-4 rounded border-background-300 text-amber-500 focus:ring-amber-400 cursor-pointer" /><label htmlFor="usePurchaseAsDue" className="text-xs text-foreground-500 cursor-pointer whitespace-nowrap">Vade = Alışveriş tarihi</label></div>
                <div><label className={labelClass}>Kefil Adı</label><input type="text" value={legacyGuarantorName} onChange={e => setLegacyGuarantorName(capitalizeWords(e.target.value))} className={inputClass} placeholder="Kefil adı soyadı" /></div>
                <div><label className={labelClass}>Kefil Telefon</label><input type="text" value={legacyGuarantorPhone} onChange={e => setLegacyGuarantorPhone(digitsOnly(e.target.value))} className={inputClass} placeholder="05XX XXX XX XX" /></div>
              </div>
            )}
          </div>
        )}

        {/* Promissory note section */}
        {!isLegacyTransfer && (
          <div className="pt-4 border-t border-background-200">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-accent-600 text-xs"></i>
                </span>
                <h4 className="text-sm font-semibold text-foreground-800">Senet Bilgisi</h4>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={createNote} onChange={e => { setCreateNote(e.target.checked); if (!e.target.checked) { setCustErrors(prev => { const next = { ...prev }; delete next.note_amount; delete next.note_issue_date; delete next.note_due_date; delete next.note_book_no; delete next.note_page_no; return next; }); } }} className="sr-only peer" />
                <div className="w-9 h-5 bg-background-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-background-50 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-background-50 after:border-background-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent-500"></div>
                <span className="ms-3 text-xs font-medium text-foreground-500">Senet kaydı oluştur</span>
              </label>
            </div>
            {createNote && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelClass}>Senet No</label><div className="w-full px-3 py-2.5 text-sm sm:text-base bg-accent-50 border border-accent-200 rounded-lg text-accent-700 font-medium">{autoNoteNo}</div><p className={helperTextClass}>Otomatik</p></div>
                <div><label className={labelClass}>Durum</label><select value={noteStatus} onChange={e => setNoteStatus(e.target.value)} className={selectClass}><option value="Bekliyor">Bekliyor</option><option value="Ödendi">Ödendi</option><option value="Gecikti">Gecikti</option><option value="İptal">İptal</option></select></div>
                <div><label className={labelClass}>Senet Tutarı (TL) <span className="text-red-400">*</span></label><CurrencyInput value={noteAmount} onChange={v => { setNoteAmount(v); setCustErrors(prev => ({ ...prev, note_amount: '' })); }} className={custErrors.note_amount ? inputErrorClass : inputClass} placeholder="0" />{custErrors.note_amount && <p className={errorTextClass}>{custErrors.note_amount}</p>}</div>
                <div><label className={labelClass}>Düzenleme Tarihi <span className="text-red-400">*</span></label><input type="date" value={noteIssueDate} onChange={e => { setNoteIssueDate(e.target.value); setCustErrors(prev => ({ ...prev, note_issue_date: '' })); }} className={custErrors.note_issue_date ? inputErrorClass : inputClass} />{custErrors.note_issue_date && <p className={errorTextClass}>{custErrors.note_issue_date}</p>}</div>
                <div><label className={labelClass}>Vade Tarihi <span className="text-red-400">*</span></label><input type="date" value={noteDueDate} onChange={e => { setNoteDueDate(e.target.value); setCustErrors(prev => ({ ...prev, note_due_date: '' })); }} className={custErrors.note_due_date ? inputErrorClass : inputClass} />{custErrors.note_due_date && <p className={errorTextClass}>{custErrors.note_due_date}</p>}</div>
                <div><label className={labelClass}>Kitap No <span className="text-red-400">*</span></label><input type="text" value={noteBookNo} onChange={e => { setNoteBookNo(e.target.value); setCustErrors(prev => ({ ...prev, note_book_no: '' })); }} className={custErrors.note_book_no ? inputErrorClass : inputClass} placeholder="Örn: Kitap-1" />{custErrors.note_book_no && <p className={errorTextClass}>{custErrors.note_book_no}</p>}</div>
                <div><label className={labelClass}>Sayfa No <span className="text-red-400">*</span></label><input type="text" value={notePageNo} onChange={e => { setNotePageNo(e.target.value); setCustErrors(prev => ({ ...prev, note_page_no: '' })); }} className={custErrors.note_page_no ? inputErrorClass : inputClass} placeholder="Örn: 15" />{custErrors.note_page_no && <p className={errorTextClass}>{custErrors.note_page_no}</p>}</div>
                <div><label className={labelClass}>Sıra No</label><input type="text" value={noteSequenceNo} onChange={e => setNoteSequenceNo(e.target.value)} className={inputClass} placeholder="Opsiyonel" /></div>
                <div><label className={labelClass}>Kefil Adı</label><input type="text" value={noteGuarantorName} onChange={e => setNoteGuarantorName(capitalizeWords(e.target.value))} className={inputClass} placeholder="Kefil adı" /></div>
                <div><label className={labelClass}>Kefil Telefon</label><input type="text" value={noteGuarantorPhone} onChange={e => setNoteGuarantorPhone(digitsOnly(e.target.value))} className={inputClass} placeholder="05XX XXX XX XX" /></div>
                <div className="sm:col-span-2"><label className={labelClass}>Açıklama</label><textarea value={noteDescription} onChange={e => setNoteDescription(e.target.value)} maxLength={500} className={`${inputClass} resize-none h-20`} placeholder="Senet açıklaması..."></textarea></div>
              </div>
            )}
          </div>
        )}

        {/* Extra bottom padding for mobile scroll */}
        <div className="h-4 sm:hidden" />
      </div>
    </ResponsiveFormModal>
  );
}